import { useMemo } from 'react';
import { useUser } from './useUser';

/**
 * Reads the current user's flattened permission set from the user context.
 * `['*']` means the platform owner (passes every server check); all checks
 * return true in that case. `organization` is set when the user is limited
 * to one organization (its owner or a member), null otherwise.
 *
 * This is for UI gating only — the server re-checks every request.
 */
export const usePermissions = () => {
  const { user } = useUser();
  const permissions = user?.permissions ?? [];
  const organization = user?.organization ?? null;

  return useMemo(() => {
    const all = permissions.includes('*');
    const set = new Set(permissions);
    const has = (name: string) => all || set.has(name);
    const hasAny = (...names: string[]) => all || names.some((n) => set.has(n));
    const hasAll = (...names: string[]) => all || names.every((n) => set.has(n));
    return { permissions, organization, has, hasAny, hasAll };
  }, [permissions, organization]);
};
